/**
 * Recipe Service - Simplified for The Weekly
 *
 * This service manages personal recipes from RecipeKeeper (497 recipes)
 * and imported recipes from Firestore (browser extension saves).
 */

import { Recipe, RecipeInstruction, ProteinCategory, getProteinCategory, getProteinIngredients } from '../types/recipe';
import personalRecipeIndex, { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import personalRecipeDetails from './personalRecipeDetails';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import importedRecipeService from './importedRecipeService';
import { getActiveHouseholdId } from './householdContext';

export interface RecipeFilter {
  cuisine?: string;
  protein?: ProteinCategory;
  maxTime?: number; // max cook time in minutes
  hasRating?: boolean;
  hasNotes?: boolean;
  searchQuery?: string;
}

/** A single Firestore document location to try when resolving a saved recipe. */
export interface RecipeLookupLocation {
  /** Full path segments to the document (collection segments + doc id). */
  segments: string[];
  /** Human label for logging/debugging which scope matched. */
  scope: 'household' | 'personal' | 'imported';
}

/**
 * Deterministic, ordered list of Firestore locations to try for a saved/imported
 * recipe id. Callers iterate in order and stop at the first hit — there is no
 * silent early bail while an id + userId are present. This is the read-side
 * counterpart to `getPersistableRecipeId`: together they guarantee that a recipe
 * whose data exists in Firestore is always found.
 *
 * Order: household savedRecipes → personal savedRecipes → legacy importedRecipes.
 * (The shared public `recipes/` catalog uses a different document shape/converter
 * and is resolved by `unifiedRecipeService`, not here.)
 *
 * Pure function — no Firestore access — so the ordering is unit-testable.
 */
export function resolveRecipeLookupPlan(
  id: string,
  userId: string | null | undefined,
  householdId: string | null | undefined
): RecipeLookupLocation[] {
  const plan: RecipeLookupLocation[] = [];
  if (householdId) {
    plan.push({ segments: ['households', householdId, 'savedRecipes', id], scope: 'household' });
  }
  if (userId) {
    plan.push({ segments: ['users', userId, 'savedRecipes', id], scope: 'personal' });
    plan.push({ segments: ['users', userId, 'importedRecipes', id], scope: 'imported' });
  }
  return plan;
}

class RecipeService {
  // Cache for recipe details
  private recipeCache = new Map<number | string, Recipe>();
  private cacheTimestamp = new Map<string, number>();
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Initialize the recipe service (loads the index)
   */
  async initialize(): Promise<void> {
    await personalRecipeIndex.loadIndex();
    console.log('RecipeService - Initialized with personal recipes');
  }

  /**
   * Get all personal recipes (index entries for fast access)
   */
  async getAllPersonalRecipes(language: 'en' | 'fr'): Promise<PersonalRecipeIndexEntry[]> {
    const recipes = await personalRecipeIndex.getAllRecipes(language);
    console.log(`RecipeService - getAllPersonalRecipes: ${recipes.length} recipes available`);
    return recipes;
  }

  /**
   * Search personal recipes by query string
   */
  async searchPersonalRecipes(query: string, language: 'en' | 'fr'): Promise<PersonalRecipeIndexEntry[]> {
    if (!query || query.trim().length === 0) {
      return this.getAllPersonalRecipes(language);
    }
    return await personalRecipeIndex.searchByText(query, 50, language);
  }

  /**
   * Get recipes filtered by various criteria
   */
  async getRecipesByFilter(filter: RecipeFilter, language: 'en' | 'fr'): Promise<PersonalRecipeIndexEntry[]> {
    // Start with all recipes
    let recipes = await this.getAllPersonalRecipes(language);

    // Filter by search query
    if (filter.searchQuery) {
      recipes = await personalRecipeIndex.searchByText(filter.searchQuery, 100, language);
    }

    // Filter by cuisine
    if (filter.cuisine) {
      const cuisineLower = filter.cuisine.toLowerCase();
      recipes = recipes.filter(r =>
        r.cuisines.some(c => c.toLowerCase().includes(cuisineLower))
      );
    }

    // Filter by max cook time
    if (filter.maxTime) {
      recipes = recipes.filter(r => r.readyInMinutes <= filter.maxTime!);
    }

    // Note: Protein category filtering requires loading full recipe details
    // which is expensive. For now, return the filtered list and let the UI
    // handle protein filtering if needed using the week generator service.

    return recipes;
  }

  /**
   * Get recipe details by ID
   */
  async getRecipeById(id: number | string, userId?: string): Promise<Recipe | null> {
    try {
      // Check cache first
      if (this.recipeCache.has(id)) {
        const cacheKey = `recipe:${id}`;
        const cacheAge = Date.now() - (this.cacheTimestamp.get(cacheKey) || 0);
        if (cacheAge < this.CACHE_EXPIRY_MS) {
          return this.recipeCache.get(id)!;
        }
        // Clear expired cache
        this.recipeCache.delete(id);
        this.cacheTimestamp.delete(cacheKey);
      }

      // Check if this is a saved/imported recipe (ID starts with "saved-" or "imported-")
      if (typeof id === 'string' && (id.startsWith('saved-') || id.startsWith('imported-'))) {
        const recipe = await this.getSavedRecipeById(id, userId);
        if (recipe) {
          this.cacheRecipe(recipe);
          return recipe;
        }
        // Not in any user/household scope. Bulk migrations promote saved/
        // imported docs to the shared catalog and remove the per-user copy,
        // so the catalog is a legitimate home for these ids — check it before
        // declaring the recipe gone (#496). The catalog only: every
        // savedRecipes scope was just read, and asking unifiedRecipeService's
        // getRecipeById read two of them again (#777).
        const catalogRecipe = await this.getCatalogRecipeById(id, userId, { sharedOnly: true });
        if (catalogRecipe) {
          this.cacheRecipe(catalogRecipe);
          return catalogRecipe;
        }
        console.warn('RecipeService - Saved recipe not found:', id);
        return null;
      }

      // Try to get from personal recipes
      if (typeof id === 'string') {
        const recipe = await personalRecipeDetails.getRecipeDetails(id);
        if (recipe) {
          this.cacheRecipe(recipe);
          return recipe;
        }
      }

      // Any other id (bulk-imported catalog ids like `katrine-…`/`nyt-…`,
      // uuid catalog recipes) lives in Firestore, not the local bundle.
      // Meal plans reference these directly, so rehydration must resolve
      // them here (#496).
      const catalogRecipe = await this.getCatalogRecipeById(String(id), userId);
      if (catalogRecipe) {
        this.cacheRecipe(catalogRecipe);
        return catalogRecipe;
      }

      console.warn('RecipeService - Recipe not found:', id);
      return null;
    } catch (error) {
      console.error('RecipeService - Error fetching recipe:', error);
      return null;
    }
  }

  /**
   * Get saved recipe from Firestore by ID
   * Checks savedRecipes first (new), then importedRecipes (legacy)
   */
  private async getSavedRecipeById(id: string, userId?: string): Promise<Recipe | null> {
    if (!isFirebaseEnabled() || !firestore) {
      console.warn('RecipeService - Firebase not enabled, cannot fetch saved recipe');
      return null;
    }

    if (!userId) {
      console.warn('RecipeService - No userId provided for saved recipe lookup');
      return null;
    }

    try {
      // Try each scope in a fixed, deterministic order and stop at the first hit.
      // No silent early bail: as long as a userId is present, every reachable
      // scope is checked (household savedRecipes → personal savedRecipes →
      // legacy importedRecipes). This is what makes rehydration reliable even
      // when the active household resolves after the initial download.
      const plan = resolveRecipeLookupPlan(id, userId, getActiveHouseholdId());
      for (const { segments } of plan) {
        const [root, scopeId, ...rest] = segments;
        const snap = await getDoc(doc(firestore, root, scopeId, ...rest));
        if (snap.exists()) {
          return importedRecipeService.convertImportedToRecipe({
            ...snap.data(),
            id: snap.id,
          } as any);
        }
      }

      return null;
    } catch (error) {
      console.error('RecipeService - Error fetching saved recipe:', error);
      return null;
    }
  }

  /**
   * Fallback resolution through `unifiedRecipeService`: active/alternate
   * savedRecipes scopes plus the shared public `recipes/` catalog. This is
   * what lets meal-plan rehydration find recipes that only exist in the
   * catalog — bulk-imported ids (`katrine-…`, `nyt-…`) and saved/imported
   * docs that a migration promoted to the catalog (#496). Lazy import keeps
   * module load order independent of the larger unified service.
   */
  private async getCatalogRecipeById(
    id: string,
    userId?: string,
    { sharedOnly = false }: { sharedOnly?: boolean } = {}
  ): Promise<Recipe | null> {
    try {
      const { default: unifiedRecipeService } = await import('./unifiedRecipeService');
      return sharedOnly
        ? await unifiedRecipeService.getSharedRecipeById(id)
        : await unifiedRecipeService.getRecipeById(id, userId);
    } catch (error) {
      console.error('RecipeService - Catalog fallback lookup failed:', error);
      return null;
    }
  }

  /**
   * Persist healed content back to the Firestore document a saved/imported
   * recipe actually lives in. Unlike `unifiedRecipeService.updateRecipe` (which
   * always targets the active-scope `savedRecipes` collection), this resolves
   * the same deterministic lookup plan used for reads and writes to whichever
   * scope holds the doc — household `savedRecipes`, personal `savedRecipes`, or
   * the legacy `importedRecipes` collection. This is what lets an `imported-`
   * recipe that self-heals on the detail screen CONVERGE (persist once) instead
   * of re-scraping on every view. Only defined fields are written (Firestore
   * rejects `undefined`). Returns true when a doc was found and updated.
   */
  async persistHealedRecipe(
    id: string,
    userId: string | undefined,
    fields: Record<string, unknown>
  ): Promise<boolean> {
    if (!isFirebaseEnabled() || !firestore || !userId) return false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) cleaned[key] = value;
    }
    if (Object.keys(cleaned).length === 0) return false;

    try {
      const plan = resolveRecipeLookupPlan(id, userId, getActiveHouseholdId());
      for (const { segments } of plan) {
        const [root, scopeId, ...rest] = segments;
        const ref = doc(firestore, root, scopeId, ...rest);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await updateDoc(ref, cleaned);
          // Invalidate the in-memory cache so the next read reflects the heal.
          this.recipeCache.delete(id);
          this.cacheTimestamp.delete(`recipe:${id}`);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.warn('RecipeService - Failed to persist healed recipe (non-critical):', error);
      return false;
    }
  }

  /**
   * Get multiple recipes by IDs (for week generation)
   */
  async getRecipesByIds(ids: string[]): Promise<Recipe[]> {
    const recipes: Recipe[] = [];

    for (const id of ids) {
      const recipe = await this.getRecipeById(id);
      if (recipe) {
        recipes.push(recipe);
      }
    }

    return recipes;
  }

  /**
   * Get random recipes for week generation
   * @param count Number of recipes to return
   * @param excludeIds Recipe IDs to exclude (e.g., from previous week)
   */
  async getRandomRecipes(count: number, excludeIds: string[] = [], language: 'en' | 'fr'): Promise<Recipe[]> {
    const allRecipes = await this.getAllPersonalRecipes(language);

    // Filter out excluded recipes
    const available = allRecipes.filter(r => !excludeIds.includes(r.id));

    // Shuffle and take count
    const shuffled = this.shuffleArray([...available]);
    const selected = shuffled.slice(0, count);

    // Load full details for selected recipes
    const recipes: Recipe[] = [];
    for (const indexEntry of selected) {
      const recipe = await this.getRecipeById(indexEntry.id);
      if (recipe) {
        recipes.push(recipe);
      }
    }

    return recipes;
  }

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  private shuffleArray<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * Cache a recipe manually (useful for planned meals)
   */
  public cacheRecipe(recipe: Recipe): void {
    this.recipeCache.set(recipe.id, recipe);
    this.cacheTimestamp.set(`recipe:${recipe.id}`, Date.now());
  }

  /**
   * Get total number of personal recipes available
   */
  async getTotalRecipes(): Promise<number> {
    return await personalRecipeIndex.getTotalRecipes();
  }

  /**
   * Check if a recipe contains protein sources
   */
  public hasProteinSource(recipe: Recipe): boolean {
    const proteinSources = [
      'chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'ham', 'sausage',
      'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'shrimp', 'crab', 'lobster',
      'eggs', 'egg', 'cheese', 'greek yogurt', 'yogurt', 'tofu', 'beans', 'lentils',
      'chickpeas', 'quinoa', 'nuts', 'peanut butter', 'almond butter'
    ];

    // Check extendedIngredients
    if (recipe.extendedIngredients) {
      for (const ingredient of recipe.extendedIngredients) {
        if (!ingredient || !ingredient.name) continue;
        const name = ingredient.name.toLowerCase();
        if (proteinSources.some(protein => name.includes(protein))) {
          return true;
        }
      }
    }

    // Check simple ingredients array
    if (recipe.ingredients) {
      for (const ingredient of recipe.ingredients) {
        if (!ingredient) continue;
        const name = ingredient.toLowerCase();
        if (proteinSources.some(protein => name.includes(protein))) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Get protein category for a recipe
   */
  public getProteinCategory(recipe: Recipe): ProteinCategory {
    return getProteinCategory(recipe);
  }

  /**
   * Get protein ingredients list from a recipe
   */
  public getProteinIngredients(recipe: Recipe): string[] {
    return getProteinIngredients(recipe);
  }

  /**
   * Detect cuisine from recipe title and ingredients
   */
  public detectCuisine(recipe: Recipe): string | undefined {
    const cuisineKeywords: Record<string, string[]> = {
      'italian': ['pasta', 'risotto', 'pizza', 'lasagna', 'parmesan', 'mozzarella', 'basil', 'oregano'],
      'mexican': ['taco', 'burrito', 'enchilada', 'salsa', 'tortilla', 'cilantro', 'cumin', 'jalapeño'],
      'chinese': ['soy sauce', 'wok', 'stir fry', 'rice', 'ginger', 'sesame', 'tofu', 'dumpling'],
      'indian': ['curry', 'masala', 'naan', 'tandoori', 'tikka', 'turmeric', 'cardamom', 'cumin'],
      'thai': ['coconut milk', 'thai', 'lemongrass', 'fish sauce', 'curry', 'basil'],
      'japanese': ['sushi', 'miso', 'teriyaki', 'sashimi', 'ramen', 'udon', 'sake', 'wasabi'],
      'french': ['croissant', 'beurre', 'coq au vin', 'ratatouille', 'crème', 'dijon'],
      'mediterranean': ['olive oil', 'feta', 'hummus', 'tahini', 'pita', 'za\'atar', 'sumac'],
    };

    const title = recipe.title.toLowerCase();
    const ingredients = [
      ...(recipe.ingredients || []),
      ...(recipe.extendedIngredients?.map(i => i.name) || [])
    ].join(' ').toLowerCase();

    const searchText = `${title} ${ingredients}`;

    for (const [cuisine, keywords] of Object.entries(cuisineKeywords)) {
      const matchCount = keywords.filter(kw => searchText.includes(kw)).length;
      if (matchCount >= 2) {
        return cuisine;
      }
    }

    return recipe.cuisines?.[0];
  }

  /**
   * Check if ingredient names match (with normalization)
   */
  public isIngredientMatch(ingredientName: string, itemName: string): boolean {
    if (!ingredientName || !itemName) return false;

    const ingredient = ingredientName.toLowerCase().trim();
    const item = itemName.toLowerCase().trim();

    // Exact match
    if (ingredient === item) return true;

    // Partial match
    if (ingredient.includes(item) || item.includes(ingredient)) return true;

    // Singular/plural handling
    const singularize = (word: string) => word.replace(/(s|es)$/, '');
    if (singularize(ingredient) === singularize(item)) return true;

    return false;
  }
}

export default new RecipeService();
